import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { ExclamationCircleOutlined } from "@ant-design/icons";

import { LOGIN_URL } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import appLogo from "../../assets/png/HRZyra-Logo.png";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  AuthSubmitButton,
  AuthTextLink,
  FormCheckbox,
  FormLabel,
  PasswordField,
} from "../../components/ui/form-fields";

const ConfirmModal = ({ open, onConfirm, onCancel }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl p-5 w-[25rem] max-w-md animate-fadeIn border border-border">
        <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          Already Logged In <ExclamationCircleOutlined />
        </h2>

        <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
          This account is already active on another device/session.
          Do you want to continue and force this login?
        </p>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" variant="brand" onClick={onConfirm}>
            Yes, Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

const TypingText = ({ texts }) => {
  const [text, setText] = useState("");
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [reverse, setReverse] = useState(false);

  useEffect(() => {
    if (index === texts.length) return;

    if (subIndex === texts[index].length + 1 && !reverse) {
      setTimeout(() => setReverse(true), 1500);
      return;
    }
    if (subIndex === 0 && reverse) {
      setReverse(false);
      setIndex((prev) => (prev + 1) % texts.length);
      return;
    }

    const timeout = setTimeout(() => {
      setSubIndex((prev) => prev + (reverse ? -1 : 1));
    }, reverse ? 50 : 100);

    setText(texts[index].substring(0, subIndex));
    return () => clearTimeout(timeout);
  }, [subIndex, index, reverse, texts]);

  return (
    <h2 className="lg:text-2xl md:text-lg font-medium text-white text-center min-h-[32px]">
      {text}
      <span className="animate-pulse">|</span>
    </h2>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const { loginauth, isAuthenticated } = useContext(AuthContext);
  const { create } = useApiMethods();

  const [loading, setLoading] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [values, setValues] = useState({ organization: "", employee_code: "", password: "" });
  const [showModal, setShowModal] = useState(false);
  const [forceLoginValues, setForceLoginValues] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      const branchAvail = sessionStorage.getItem("branch_avail") === "true";
      navigate(branchAvail ? "/branches" : "/home");
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => setValues({ ...values, [e.target.name]: e.target.value });

  const buildLoginPayload = (formValues, forced = false) => ({
    employee_code: formValues.employee_code.trim().toUpperCase(),
    password: formValues.password,
    organization: formValues.organization.trim().toUpperCase(),
    ...(forced ? { allow_login: true } : {}),
  });

  const handleLogin = async (payload, forced = false) => {
    try {
      const response = await create(LOGIN_URL, payload, false, setLoading);

      if (response?.status === "success") {
        loginauth(response.data, keepSignedIn);
        navigate(response.data?.branch_avail ? "/branches" : "/home");
      } else if (response?.status === "fail") {
        if (response.message === "User already logged in" && !forced) {
          setForceLoginValues(payload);
          setShowModal(true);
        } else {
          ToastMsg("error", response.message);
        }
      }
    } catch (error) {
      ToastMsg("error", error.message || "Something went wrong");
    }
  };

  const onFinish = (e) => {
    e.preventDefault();
    if (!values.organization?.trim()) {
      ToastMsg("error", "Please enter organization");
      return;
    }
    if (!values.employee_code?.trim()) {
      ToastMsg("error", "Please enter employee code");
      return;
    }
    handleLogin(buildLoginPayload(values));
  };

  const handleForceLogin = () => {
    setShowModal(false);
    handleLogin({ ...forceLoginValues, allow_login: true }, true);
  };

  return (
    <div className="h-screen grid grid-cols-12 font-sans bg-background text-foreground">
      <div className="hidden lg:flex col-span-5 animated-gradient text-white flex-col justify-center items-center p-10 animate-gradient bg-[length:400%_400%] relative overflow-hidden">
        <img
          src="https://cdn-icons-png.flaticon.com/512/3135/3135675.png"
          alt="HRMS Illustration"
          className="absolute opacity-10 w-[30%] h-[30%] object-contain"
        />
        <TypingText
          texts={[
            "Welcome to HRZyra",
            "Your smarter way to manage HR & Payroll",
            "Simplify attendance, payroll, and compliance",
            "Empowering employees with smarter HR tools",
            "Seamless HR solutions for growing businesses",
          ]}
        />
      </div>

      <div className="col-span-full lg:col-span-7 relative flex items-center justify-center p-6">
        <div className="absolute inset-0 bg-[url('https://media.istockphoto.com/id/1847571768/photo/customer-with-bank-manager-in-office-stock-photo.jpg?s=1024x1024&w=is&k=20&c=Y3oAUjmWSEipuQoTva74TkZuZPYSwT9TFsMz-z1R0Kw=')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-white/40 backdrop-blur-sm dark:bg-black/50" />

        <div className="w-full max-w-sm relative z-10">
          <div className="flex justify-center mb-6">
            <img src={appLogo} alt="App Logo" className="h-10 w-auto animate-fadeIn" />
          </div>

          <div className="bg-card/95 backdrop-blur-md rounded-xl shadow-xl p-6 animate-fadeIn border border-border">
            <h2 className="text-center text-lg font-semibold text-foreground mb-1">Sign In</h2>
            <p className="text-center text-sm text-muted-foreground mb-5">
              Welcome back! Please enter your details.
            </p>

            <form onSubmit={onFinish} className="space-y-4">
              <div className="space-y-1.5">
                <FormLabel htmlFor="organization">Organization</FormLabel>
                <Input
                  id="organization"
                  type="text"
                  name="organization"
                  value={values.organization}
                  onChange={handleChange}
                  autoComplete="off"
                  autoFocus
                  placeholder="e.g. EMED"
                  className="uppercase"
                />
              </div>

              <div className="space-y-1.5">
                <FormLabel htmlFor="employee_code">Employee Code</FormLabel>
                <Input
                  id="employee_code"
                  type="text"
                  name="employee_code"
                  value={values.employee_code}
                  onChange={handleChange}
                  autoComplete="off"
                  placeholder="e.g. EMED033"
                  className="uppercase"
                />
              </div>

              <PasswordField
                id="password"
                name="password"
                label="Password"
                value={values.password}
                onChange={handleChange}
                placeholder="Enter your password"
              />

              <div className="flex items-center justify-between gap-3">
                <FormCheckbox
                  label="Remember Me"
                  checked={keepSignedIn}
                  onChange={(e) => setKeepSignedIn(e.target.checked)}
                />
                <AuthTextLink to="/forgotpassword">Forgot Password?</AuthTextLink>
              </div>

              <AuthSubmitButton loading={loading} loadingText="Signing In...">
                Sign In
              </AuthSubmitButton>

              <p className="text-center text-sm text-muted-foreground pt-1">
                New organization?{" "}
                <AuthTextLink to="/administrator/company/add">Create one</AuthTextLink>
              </p>
            </form>
          </div>
        </div>
      </div>

      <ConfirmModal open={showModal} onConfirm={handleForceLogin} onCancel={() => setShowModal(false)} />
    </div>
  );
};

export default Login;
